import { StallError } from "@lib/utils/stallGuard";
import { bumpSyncActivity } from "./syncActivityBeacon";

/**
 * `AbortSignal.timeout(ms)`, built from primitives that exist everywhere we
 * run. The static helper is Safari/WebKit 16+, and the iPad app's WKWebView
 * floor is iOS 15 — where referencing it throws synchronously, which would
 * break the caller outright instead of bounding it.
 */
function timeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(
      new DOMException(`Timed out after ${ms}ms`, "TimeoutError"),
    );
  }, ms);
  // Don't hold the timer past an outside abort or process teardown concerns:
  // clearing on abort keeps long-lived pages from accumulating timers when
  // requests settle early. (Settling early does not abort the signal, so the
  // timer then runs out harmlessly against an already-finished request.)
  controller.signal.addEventListener("abort", () => clearTimeout(timer), {
    once: true,
  });
  return controller.signal;
}

// Catch-up fetches run inside the sync engine's serial action pump: a request
// that never settles (black-holed connection, socket killed by backgrounding)
// would wedge the pump and silently stop the user's own edits from saving.
// Bound them, and surface hitting the bound as a StallError so the engine
// opens its stall episode — the server re-delivers unacked ranges faster
// than a full fetch timeout drains, so an "ordinary error" per delivery
// would still starve edits queued behind the retries.
//
// The bound is deliberately GENEROUS. It does not limit pump occupancy (the
// stall guard does that, and a timed-out fetch keeps running orphaned), so
// its only job is to eventually reap requests that will never settle. A
// tight bound would convert a legitimately slow catch-up — a days-offline
// gap on a bad connection — into a livelock: every re-delivered retry
// aborts at the same bound and the confirmed version never advances until
// the user reloads. Every retry pays the full bound from zero, so make it
// comfortably larger than any plausible slow-but-succeeding response.
export const CATCH_UP_FETCH_TIMEOUT_MS = 120_000;

/**
 * Run one bounded catch-up request with the liveness-beacon contract:
 *
 * - The start and any self-settled outcome (success OR a genuine network
 *   error) bump the sync activity beacon — the stall guard must read a
 *   bounded-but-slow or cleanly-failing fetch as progress, not a stall.
 * - An exhausted timeout does NOT bump — it is the opposite of liveness.
 *   The guard orphans timed-out work but the orphan keeps running to its
 *   abort; if that abort counted as progress, each orphaned fetch from a
 *   piled-up episode would reset the probe window and let re-delivered
 *   confirms occupy the pump far past the probe budget.
 * - An exhausted timeout surfaces as a StallError so the client opens the
 *   stall episode (see Client's handleMessage pump).
 */
export async function runBoundedCatchUpFetch<T>(
  fetchFn: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number = CATCH_UP_FETCH_TIMEOUT_MS,
): Promise<T> {
  bumpSyncActivity();
  const signal = timeoutSignal(timeoutMs);
  catchUpSignals.add(signal);

  try {
    return await fetchFn(signal);
  } catch (error) {
    if (signal.aborted) {
      throw new StallError(`catch-up fetch exceeded ${timeoutMs}ms`);
    }

    throw error;
  } finally {
    if (!signal.aborted) {
      bumpSyncActivity();
    }
  }
}

// Start/settle bumps alone leave a liveness gap: the guard's first window
// consumes the start bump, so a slow-but-alive download spanning two windows
// would read as a stall even though bytes are flowing and the generous fetch
// bound is exactly for that case. Chunk arrivals are the honest mid-flight
// signal — real bytes are real progress; a black-holed request produces no
// chunks and still trips the guard. The signal registry scopes the chunk
// beacon to catch-up requests only (tRPC forwards the query signal to fetch
// by identity): unrelated network chatter must not read as sync progress, or
// a wedged apply under a busy page would never be detected.
const catchUpSignals = new WeakSet<AbortSignal>();

export function isCatchUpFetchSignal(
  signal: AbortSignal | null | undefined,
): boolean {
  return signal != null && catchUpSignals.has(signal);
}

/**
 * Wrap a fetch implementation so responses to catch-up requests (identified
 * by their {@link runBoundedCatchUpFetch} signal) bump the activity beacon on
 * arrival of headers and of every body chunk. All other requests pass
 * through untouched.
 */
export function withCatchUpChunkBeacon(
  baseFetch: (
    input: RequestInfo | URL,
    init?: RequestInit,
  ) => Promise<Response>,
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return async (input, init) => {
    const response = await baseFetch(input, init);

    if (!isCatchUpFetchSignal(init?.signal)) {
      return response;
    }

    bumpSyncActivity();

    try {
      if (response.body == null) {
        return response;
      }

      const monitored = response.body.pipeThrough(
        new TransformStream<Uint8Array, Uint8Array>({
          transform(chunk, controller) {
            bumpSyncActivity();
            controller.enqueue(chunk);
          },
        }),
      );
      return new Response(monitored, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    } catch {
      // Older WebKit may lack streaming Response construction; the fetch
      // still works, it just falls back to start/settle-only liveness.
      return response;
    }
  };
}
